import { useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { useBreakpointValue } from '@chakra-ui/react';
import { useWindowSize } from 'usehooks-ts';
import type { SwiperRef } from 'swiper/react';
import { useDebouncedSwiperWidth } from './useDebouncedSwiperWidth';
import {
    CARD_BASE_WIDTH,
    CARD_DM_WIDTH,
    SLIDE_SPACING,
    getCarouselItemsLimit,
} from './utils/getCarouselItemsLimit';
import { getVisibleItems } from './utils/getVisibleItems';
import type { Products } from '../../bff-api/products/model';

export const useDynamicCarouselConfig = ({
    ref,
    products,
}: {
    ref: MutableRefObject<SwiperRef | null>;
    products: Products;
}): { slidesPerGroup: number; limitedProducts: Products } => {
    const cardWidth =
        useBreakpointValue({
            base: CARD_BASE_WIDTH,
            sm: CARD_DM_WIDTH,
        }) ?? CARD_BASE_WIDTH;

    const swiperWidth = useDebouncedSwiperWidth(ref, 300);

    const slidesPerGroup = getVisibleItems(swiperWidth ?? cardWidth, cardWidth, SLIDE_SPACING);
    const { width: windowWidth } = useWindowSize();

    const carouselItemLimit: number = useMemo(
        () => getCarouselItemsLimit(windowWidth, swiperWidth ?? CARD_BASE_WIDTH),
        [windowWidth, swiperWidth],
    );

    const limitedProducts = products.slice(0, carouselItemLimit);

    return { slidesPerGroup, limitedProducts };
};
